import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { inquiryService } from '../services/inquiryService';
import type { InquiryFilterParams, ReplyToInquiryRequest } from '../types/inquiry';

export const INQUIRIES_QUERY_KEY = 'inquiries';

/**
 * Fetches a paginated, filtered list of inquiries.
 * Query key includes all filter + pagination params so each unique combination
 * is cached independently.
 */
export const useInquiries = (
  filter: InquiryFilterParams = {},
  page = 0,
  size = 20,
) =>
  useQuery({
    queryKey: [INQUIRIES_QUERY_KEY, { ...filter, page, size }] as const,
    queryFn: () => inquiryService.listInquiries(filter, { page, size }),
  });

/** Mutation: mark an inquiry as READ. Invalidates the full inquiries cache on success. */
export const useMarkAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inquiryId: string) => inquiryService.markAsRead(inquiryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INQUIRIES_QUERY_KEY] });
    },
  });
};

/** Mutation: send an email reply. Invalidates the full inquiries cache on success. */
export const useReplyToInquiry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ inquiryId, request }: { inquiryId: string; request: ReplyToInquiryRequest }) =>
      inquiryService.replyToInquiry(inquiryId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INQUIRIES_QUERY_KEY] });
    },
  });
};

/** Mutation: mark as REPLIED without sending email. Invalidates the full inquiries cache on success. */
export const useMarkAsReplied = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inquiryId: string) => inquiryService.markAsReplied(inquiryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INQUIRIES_QUERY_KEY] });
    },
  });
};
